/* CBH — Scrolling text loop (seamless + auto-repeat enough to fill viewport) */
(() => {
  'use strict';

  const SELECTOR = '[data-cbh-marquee]';
  const controllers = new WeakMap();

  const prefersReducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  const cancelMeasureToken = (token) => {
    if (!token) return;
    if (token.kind === 'idle' && window.cancelIdleCallback) window.cancelIdleCallback(token.id);
    if (token.kind === 'raf') window.cancelAnimationFrame(token.id);
  };

  function initialise(root) {
    if (!root || controllers.has(root)) return;

    const track = root.querySelector('[data-marquee-track]');
    const group = root.querySelector('[data-marquee-group]');
    const viewport = root.querySelector('.cbh-marquee__viewport');
    const button = root.querySelector('.cbh-marquee__toggle');

    if (!track || !group || !viewport) return;

    let destroyed = false;
    let measureToken = null;
    let resizeObserver = null;

    const setPaused = (paused) => {
      root.dataset.paused = paused ? 'true' : 'false';
      if (!button) return;
      button.setAttribute('aria-pressed', paused ? 'true' : 'false');
      const label = button.querySelector('.cbh-marquee__toggle-label');
      if (label) label.textContent = paused ? 'Play' : 'Pause';
    };

    const onToggle = () => setPaused(root.dataset.paused !== 'true');
    const onPointerEnter = () => setPaused(true);
    const onPointerLeave = () => {
      if (!prefersReducedMotion()) setPaused(false);
    };
    const onFocusIn = () => setPaused(true);
    const onFocusOut = () => {
      if (!prefersReducedMotion()) setPaused(false);
    };

    const disableFocusWithin = (element) => {
      element.querySelectorAll('a, button, input, select, textarea').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });
    };

    const rebuild = () => {
      if (destroyed || !root.isConnected) return;

      const html = group.innerHTML.trim();
      if (!html) return;

      const setWidth = Math.ceil(group.scrollWidth);
      const viewportWidth = Math.ceil(viewport.clientWidth || document.documentElement.clientWidth);
      if (!setWidth || setWidth < 10) return;

      track.querySelectorAll('.cbh-marquee__group[aria-hidden="true"]').forEach((node) => node.remove());

      const desiredWidth = Math.max(viewportWidth * 2, setWidth * 2);
      let copies = Math.ceil(desiredWidth / setWidth) + 1;
      copies = Math.max(2, Math.min(10, copies));

      for (let index = 1; index < copies; index += 1) {
        const clone = document.createElement('div');
        clone.className = 'cbh-marquee__group';
        clone.setAttribute('aria-hidden', 'true');
        clone.innerHTML = html;
        disableFocusWithin(clone);
        track.appendChild(clone);
      }

      const speed = Math.max(
        30,
        parseFloat(getComputedStyle(root).getPropertyValue('--cbh-marquee-speed')) || 70
      );

      root.style.setProperty('--cbh-marquee-distance', `${setWidth}px`);
      root.style.setProperty('--cbh-marquee-duration', `${Math.max(6, setWidth / speed)}s`);
      root.dataset.cbhCopies = String(copies);
      root.dataset.cbhSetWidth = String(setWidth);
      root.dataset.cbhViewport = String(viewportWidth);
    };

    const measureSoon = () => {
      if (destroyed) return;
      cancelMeasureToken(measureToken);

      if (window.requestIdleCallback) {
        const id = window.requestIdleCallback(() => {
          measureToken = null;
          rebuild();
        }, { timeout: 1500 });
        measureToken = { kind: 'idle', id };
      } else {
        const id = window.requestAnimationFrame(() => {
          measureToken = null;
          rebuild();
        });
        measureToken = { kind: 'raf', id };
      }
    };

    const onWindowLoad = () => measureSoon();
    const onWindowResize = () => measureSoon();

    if (prefersReducedMotion()) setPaused(true);

    button?.addEventListener('click', onToggle);

    if (root.dataset.pausehover !== 'false') {
      root.addEventListener('pointerenter', onPointerEnter);
      root.addEventListener('pointerleave', onPointerLeave);
      root.addEventListener('focusin', onFocusIn);
      root.addEventListener('focusout', onFocusOut);
    }

    window.addEventListener('load', onWindowLoad, { once: true });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(measureSoon);
      resizeObserver.observe(viewport);
      resizeObserver.observe(group);
    } else {
      window.addEventListener('resize', onWindowResize, { passive: true });
    }

    document.fonts?.ready?.then(() => {
      if (!destroyed) measureSoon();
    }).catch(() => {});

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      cancelMeasureToken(measureToken);
      measureToken = null;

      button?.removeEventListener('click', onToggle);
      root.removeEventListener('pointerenter', onPointerEnter);
      root.removeEventListener('pointerleave', onPointerLeave);
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('load', onWindowLoad);
      window.removeEventListener('resize', onWindowResize);
      resizeObserver?.disconnect();
      resizeObserver = null;
      controllers.delete(root);
      delete root.dataset.cbhInit;
    };

    root.dataset.cbhInit = '1';
    controllers.set(root, { destroy });
    measureSoon();
  }

  const rootsWithin = (scope = document) => {
    const roots = [];
    if (scope.matches?.(SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(SELECTOR).forEach((root) => roots.push(root));
    return roots;
  };

  const boot = (scope = document) => rootsWithin(scope).forEach(initialise);

  const cleanup = (scope) => {
    rootsWithin(scope).forEach((root) => controllers.get(root)?.destroy?.());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
})();
